import type { JSX } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { PRODUCT_TITLE } from '../../config/tenant'
import { EXAMPLE_QUESTIONS } from '../grounding/knowledge'
import type { DatasetOverview, OverviewPage } from '../grounding/datasetOverview'

/**
 * Welcome content shown while the conversation is empty (D3: data-aware
 * templated welcome). With a built overview it renders a deterministic
 * terrain summary — coverage window, page count, KPI families, one
 * current-page capability line. Metadata counts only, no measure values
 * (the overview cannot carry figures by construction). Without an overview
 * (still building, or build failed) it renders the static v1 copy.
 */

// Pinned root→family mapping (spec §5.4). The four ratio roots are the v2
// Metric formulas set; actual* = actuals; nsp = spend; everything else is
// model-attributed volume.
const RATIO_ROOTS = new Set(['sales_roi', 'sales_cpo', 'sales_opc', 'sales_mediashare'])

export function summarizeKpiFamilies(pages: OverviewPage[]): string {
  const roots = new Set<string>()
  for (const p of pages) for (const kpi of p.kpis) roots.add(kpi.split('__')[0]!)
  const families: string[] = []
  if ([...roots].some((r) => r.startsWith('actual'))) families.push('actuals')
  if (roots.has('nsp')) families.push('spend')
  if ([...roots].some((r) => !r.startsWith('actual') && r !== 'nsp' && !RATIO_ROOTS.has(r)))
    families.push('model-attributed volumes')
  if ([...roots].some((r) => RATIO_ROOTS.has(r))) families.push('RoI/efficiency ratios')
  const list =
    families.length <= 1
      ? (families[0] ?? '')
      : families.length === 2
        ? `${families[0]} and ${families[1]}`
        : `${families.slice(0, -1).join(', ')}, and ${families.at(-1)}`
  return `${roots.size} KPIs — ${list}`
}

/** One pinned capability line per page for the welcome's "you're here" sentence. */
const PAGE_WELCOME_LINES: Record<string, string> = {
  ReviewKpis: 'track headline KPIs and compare periods',
  MarketingInvests: 'break down media spend and compare periods',
  CampaignPerformance: 'rank media RoI and compare multiple timeframes',
  Contribution: 'decompose sales into media and base contributions',
  Dynamics: 'follow KPI dynamics over time across panels',
  MultiDeepDive: 'cross-cut several KPIs and dimensions at once',
}

/** Human titles for the welcome's "you're here" sentence (ids are internal). */
const PAGE_DISPLAY_TITLES: Record<string, string> = {
  ReviewKpis: 'Review KPIs',
  MarketingInvests: 'Marketing Invests',
  CampaignPerformance: 'Campaign Performance',
  Contribution: 'Contribution',
  Dynamics: 'Dynamics',
  MultiDeepDive: 'Multi Deep Dive',
}

export function WelcomeBlock({
  onAsk,
  overview,
  pageId,
}: {
  onAsk: (question: string) => void
  overview: DatasetOverview | null
  pageId: string | null
}): JSX.Element {
  const pageLine = pageId !== null ? PAGE_WELCOME_LINES[pageId] : undefined
  // Fallback to the raw id keeps an unknown page loud rather than blank.
  const pageTitle = pageId !== null ? (PAGE_DISPLAY_TITLES[pageId] ?? pageId) : null

  return (
    <Box
      sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {PRODUCT_TITLE} MMM Advisor
      </Typography>
      {overview !== null ? (
        <>
          <Typography variant="body2" color="text.secondary">
            I&apos;m looking at your live dataset: daily data from{' '}
            {overview.timeframe.minDate ?? 'the model start'} to{' '}
            {overview.timeframe.maxDate ?? 'today'} across {overview.pages.length} analytics pages ·{' '}
            {summarizeKpiFamilies(overview.pages)}.
          </Typography>
          {pageLine !== undefined && (
            <Typography variant="body2" color="text.secondary">
              You&apos;re on {pageTitle} — here I can {pageLine}, or pull figures from any other
              page for you.
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            I&apos;m grounded in your marketing-mix model and its live data. I can:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { typography: 'body2', my: 0.25 } }}>
            <li>Explain your MMM results, KPIs and model concepts</li>
            <li>Fetch live figures from your data — never invented numbers</li>
            <li>Adjust the view or take you to the right page for your question</li>
          </Box>
        </>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Try asking:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {EXAMPLE_QUESTIONS.map((q) => (
          <Button
            key={q}
            variant="outlined"
            size="small"
            onClick={() => onAsk(q)}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
          >
            {q}
          </Button>
        ))}
      </Box>
    </Box>
  )
}
